import { CalendarHeart } from "lucide-react";
import type { DateItem } from "./api";

/** Shared display helpers for the universal-dates surfaces (Today
 *  "Upcoming" strip, the /dates anchor, and the Person card section).
 *  Keeping these in one place so the relative chip, the ICS export,
 *  and the inferred treatment stay consistent across every surface.
 *
 *  Memso voice throughout: no em dashes, "weave / recall" lexicon,
 *  product is Memso. */

/** "today" / "tomorrow" / "in 3 days" / "in 2 weeks". Past dates
 *  (negative days_until, e.g. a just-passed birthday before the
 *  next-occurrence roll) read as "today" to avoid a confusing
 *  "in -1 days". */
export function relativeDayChip(daysUntil: number): string {
  if (daysUntil <= 0) return "today";
  if (daysUntil === 1) return "tomorrow";
  if (daysUntil < 14) return `in ${daysUntil} days`;
  if (daysUntil < 60) return `in ${Math.round(daysUntil / 7)} weeks`;
  return `in ${Math.round(daysUntil / 30)} months`;
}

/** Human date for the next occurrence, e.g. "Jun 14". */
export function formatNextOccurrence(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** When the original date_value carries a 4-digit year, surface a
 *  "since 2019" affordance for recurring dates so the year-over-year
 *  history is legible. Returns null when the year is unknown
 *  ('--MM-DD') or the date is not recurring. */
export function originYearLabel(item: DateItem): string | null {
  if (!item.is_recurring) return null;
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(item.date_value);
  if (!m) return null;
  const year = Number(m[1]);
  const thisYear = new Date().getFullYear();
  if (!Number.isFinite(year) || year >= thisYear) return null;
  return `since ${year}`;
}

/** Small calendar glyph used when a date has no associated person. */
export function DateGlyph() {
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-pink-400 to-rose-500 text-white shadow-sm flex-shrink-0"
      aria-hidden="true"
    >
      <CalendarHeart className="w-5 h-5" strokeWidth={2.5} />
    </div>
  );
}

/** Avatar for a date's person. Copied small from People.tsx (the
 *  helper there is not exported and keys off a different shape).
 *  Inferred dates get the dashed amber ring per the inferred-vs-
 *  confirmed pattern. */
export function DatePersonAvatar({
  person,
  inferred,
}: {
  person: NonNullable<DateItem["person"]>;
  inferred: boolean;
}) {
  const inner = person.avatar_url ? (
    <img
      src={person.avatar_url}
      alt=""
      className="w-10 h-10 rounded-full object-cover bg-slate-200 dark:bg-slate-700"
    />
  ) : (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
      style={{
        background: `linear-gradient(135deg, hsl(${hashHue(person.display_name)}, 70%, 55%), hsl(${(hashHue(person.display_name) + 40) % 360}, 70%, 50%))`,
      }}
      aria-hidden="true"
    >
      {initialsFor(person.display_name)}
    </div>
  );
  if (!inferred) return <div className="flex-shrink-0">{inner}</div>;
  return (
    <div
      className="rounded-full p-0.5 border-2 border-dashed border-amber-400 dark:border-amber-500 flex-shrink-0"
      title="Inferred date. Open to confirm."
    >
      {inner}
    </div>
  );
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/** Pretty kind label: "birthday" -> "Birthday", "work_anniversary"
 *  -> "Work anniversary". */
export function kindLabel(kind: string): string {
  const s = kind.replace(/_/g, " ").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "Date";
}

/** Build and download a single-event .ics for a date. v1 client-side
 *  export so "Add to calendar" works without a backend round-trip.
 *  Recurring dates get an RRULE:FREQ=YEARLY. */
export function downloadIcs(item: DateItem) {
  const dt = item.next_occurrence.replace(/-/g, ""); // YYYYMMDD
  const uid = `${item.date_id}@memso`;
  const stamp =
    new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  const title = escapeIcs(
    item.person ? `${item.display_name} (${kindLabel(item.kind)})` : item.display_name,
  );
  const desc = escapeIcs(item.description || `${kindLabel(item.kind)} woven by Memso.`);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Memso//Universal Dates//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${dt}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${desc}`,
  ];
  if (item.is_recurring) lines.push("RRULE:FREQ=YEARLY");
  lines.push("END:VEVENT", "END:VCALENDAR");
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slugify(item.display_name) || "memso-date"}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function escapeIcs(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}
